"use client";

import { PlanVo, api } from "@/api";
import { ModalHeader } from "@/component/ModalHeader";
import {
  AUTO_SHOW_STAY_DURATION_MS,
  firstPurchasePromoStore,
} from "@/store/firstPurchasePromo";
import { pricingStore } from "@/store/pricing";
import { userStore } from "@/store/user";
import { useRouter } from "@bprogress/next";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { VidMuseEvent } from "../../common/events";
import { logger } from "../../common/logger";
import { isFreePlan } from "./Pricing.helpers";
import ArrowLinkIcon from "./assets/arrow-link.svg";
import { BillingCycleToggle } from "./components/BillingCycleToggle";
import { CompareTable } from "./components/CompareTable";
import { CreditPackageSection } from "./components/CreditPackageSection";
import { FAQSection } from "./components/FAQSection";
import { FirstPurchaseBanner } from "./components/FirstPurchaseBanner";
import {
  PlanListSection,
  type BillingCycle,
} from "./components/PlanListSection";
import { PricingSkeleton } from "./components/PricingSkeleton";
import styles from "./index.module.css";

interface PricingViewProps {
  onClose?: () => void;
}

export const PricingView = observer(function PricingView({
  onClose,
}: PricingViewProps) {
  const {
    creditPackages: storeCreditPackages,
    planPage: storePlanPage,
    loading,
  } = pricingStore;
  const router = useRouter();
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("YEARLY");
  const planListRef = useRef<HTMLDivElement>(null);
  const [subscriptionBonusMap, setSubscriptionBonusMap] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    // 只有在没有加载过时才进行 fetch
    if (!pricingStore.loaded && !pricingStore.loading) {
      pricingStore.fetchPricingData();
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      logger.event(VidMuseEvent.FreeUserStayPricingPageFor100s);
      // 只有在满足资格且未显示过时，才尝试显示
      firstPurchasePromoStore.tryShow();
    }, AUTO_SHOW_STAY_DURATION_MS);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchPromotion = async () => {
      try {
        const { data } = await api.promotions.getActivePromotions();
        const promotion = data?.subscriptionBonusPromotion;

        if (!promotion?.startTime || !promotion?.endTime) {
          setSubscriptionBonusMap({});
          return;
        }

        const now = Date.now();
        const start = new Date(promotion.startTime).getTime();
        const end = new Date(promotion.endTime).getTime();

        if (Number.isNaN(start) || Number.isNaN(end)) {
          setSubscriptionBonusMap({});
          return;
        }

        const isActive = now >= start && now <= end;

        if (cancelled) {
          return;
        }

        setSubscriptionBonusMap(
          isActive ? (promotion.memberBonusMap ?? {}) : {},
        );
      } catch (error) {
        console.error("Failed to fetch subscription bonus promotion:", error);
      }
    };

    fetchPromotion();

    return () => {
      cancelled = true;
    };
  }, []);

  const { freeCreditAmount, yearlyDiscount, planList = [] } = storePlanPage;
  const { creditPackageList = [] } = storeCreditPackages;

  const currentPlanCode = userStore.regularPlan?.planCode;
  const getPlanBonus = (plan: PlanVo) => {
    const normalizedCode = plan.memberCode || "";
    return subscriptionBonusMap[normalizedCode] ?? 0;
  };

  const planListByBillingCycle = useMemo(() => {
    const freePlan = planList.find((plan) => isFreePlan(plan));

    const otherPlans = planList.filter(
      (plan) => !isFreePlan(plan) && plan.intervalType === billingCycle,
    );

    return freePlan ? [freePlan, ...otherPlans] : otherPlans;
  }, [planList, billingCycle]);

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    router.push("/");
  };

  const hasPlanData = planList.length > 0;
  const hasCreditData = creditPackageList.length > 0;
  const shouldShowLoading = loading && !(hasPlanData && hasCreditData);

  const handleClaimPromo = () => {
    // 在 Pricing 页面点击 Claim 后滚动到 Plan 列表
    planListRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <div className={classNames("pb-24 w-full h-fit", styles.page)}>
        <ModalHeader onClose={handleClose} />

        <div className="max-w-[1166px] mx-auto px-5 box-content">
          {firstPurchasePromoStore.shouldShowPromo && (
            <FirstPurchaseBanner onClaim={handleClaimPromo} />
          )}
        </div>

        <h1 className="mt-5 text-center text-4xl font-bold text-text-primary leading-normal">
          Manage Subscription
        </h1>

        <BillingCycleToggle value={billingCycle} onChange={setBillingCycle} />

        <div className="mt-4 text-center flex items-center justify-center">
          <p
            className="text-white cursor-pointer"
            onClick={() => {
              document
                .getElementById("add-ons")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <span className="mr-3">Need more credits?</span>
            <span className="font-bold mr-1">See credits plan</span>
            <ArrowLinkIcon className="inline-block w-[10px] h-[10px]" />
          </p>
        </div>

        {shouldShowLoading ? (
          <PricingSkeleton />
        ) : (
          <>
            <div
              className="scroll-mt-[100px]"
              data-section="plan-list"
              ref={planListRef}
            >
              <PlanListSection
                plans={planListByBillingCycle}
                billingCycle={billingCycle}
                freeCreditAmount={freeCreditAmount}
                currentPlanCode={currentPlanCode}
                yearlyDiscount={yearlyDiscount}
                getPlanBonus={getPlanBonus}
              />
            </div>

            <CompareTable />

            <CreditPackageSection
              creditPackageList={creditPackageList}
              currentPlanCode={currentPlanCode}
            />

            <FAQSection />
          </>
        )}
      </div>
    </>
  );
});
